import { Currency, Form, SingleDatePicker } from '@mairistem/composants';
import { useUpdateEffect } from '@mairistem/tools';
import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';
import InputNumber from '@/components/Autre/InputNumber';
import type { TicketType } from '@/types';

interface TicketsProps {
  type: string;
  dateValidite: string;
  nombre: number;
  montantUnitaire: number;
  disabled: boolean;
  reste: number;
  onChange: (value: TicketType, complet: boolean) => void;
  onError?: (error: string) => void;
}

const Tickets = (props: TicketsProps) => {
  const {
    type: typeProps,
    dateValidite: dateProps,
    nombre: nombreProps,
    montantUnitaire: montantUnitaireProps,
    disabled,
    reste,
    onChange,
    onError,
  } = props;

  const [type, setType] = React.useState<string>(typeProps || '');
  const [date, setDate] = React.useState<dayjs.Dayjs>(dateProps ? dayjs(dateProps) : null);
  const [nombre, setNombre] = React.useState<number>(nombreProps);
  const [montantUnitaire, setMontantUnitaire] = React.useState<number>(montantUnitaireProps || 0);
  const [total, setTotal] = React.useState<number>(0);

  const handleChange = (value, setter) => {
    setter(value);
  };

  const isComplet = React.useCallback(
    () =>
      !_.isEmpty(type) &&
      !_.isNil(date) &&
      !_.isEmpty(nombre) &&
      montantUnitaire > 0 &&
      total <= reste,
    [type, date, nombre, montantUnitaire, total, reste],
  );

  useUpdateEffect(() => {
    if (!disabled) {
      onChange(
        {
          ...(type !== '' && { type }),
          ...(date !== null && { dateValidite: date?.format('DD/MM/YYYY') }),
          ...(nombre !== 0 && { nombre }),
          ...(montantUnitaire !== 0 && { montantUnitaire }),
          ...(total !== 0 && { montant: total }),
        },
        isComplet(),
      );
    }
  }, [disabled, type, date, nombre, montantUnitaire, total, isComplet]);

  useUpdateEffect(() => {
    if (nombre !== 0 && montantUnitaire !== 0) {
      // Arrondi au centime : 3 × 7,10 vaut 21.299999999999997 en flottant.
      const t = parseFloat((nombre * montantUnitaire).toFixed(2));
      if (t > reste && onError) {
        onError('Le total ne peut pas être supérieur au reste');
      }

      setTotal(t);
    }
  }, [nombre, montantUnitaire]);

  return (
    <Form.Group style={{ marginTop: 0, marginBottom: 0 }}>
      <Form.Input
        disabled={disabled}
        autoFocus={!disabled}
        value={type}
        label="Type"
        inline
        style={{ width: 180 }}
        onChange={(e, { value }) => handleChange(value, setType)}
        input={{ 'data-testid': 'typeTickets' }}
        required
      />
      <Form.Field inline required>
        <label>Date de validité</label>
        <SingleDatePicker
          data-testid="dateValiditeTickets"
          disabled={disabled}
          clearable
          value={date}
          onChange={(val: dayjs.Dayjs) => setDate(val)}
          disabledDate={(d) => d.isBefore(dayjs().startOf('day'))}
        />
      </Form.Field>
      <InputNumber
        data-testid="nombreTickets"
        disabled={disabled}
        value={nombre}
        maxLength={2}
        onChange={(value) => handleChange(value, setNombre)}
        required
      />
      <Currency
        data-testid="montantUnitaireTickets"
        disabled={disabled}
        value={montantUnitaire}
        label="Montant unitaire"
        inline
        placeholder=""
        style={{ width: 100 }}
        valueMin={0}
        description=""
        onBlur={(e, value) => handleChange(value, setMontantUnitaire)}
        required
      />
      <Currency
        readOnly={!disabled}
        disabled={disabled}
        value={total}
        label="Total"
        inline
        error={!disabled && total > reste}
        placeholder=""
        style={{ width: 100 }}
        valueMin={0}
        valueMax={reste}
        description=""
      />
    </Form.Group>
  );
};

export default Tickets;
